import { parse } from '@fleequid-logs/query';
import type { ReactNode } from 'react';

// Privacy mode (P): addresses, emails and the line's user behind hatched redaction bars, for screen shares (D-12).
const PII = /\b\d{1,3}(?:\.\d{1,3}){3}\b|[\w.+*-]+@[\w-]+(?:\.[\w-]+)+/g;

const escapeRegex = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// The free-text words of a query, to light up in the lines.
export function queryWords(q: string): string[] {
    const words: string[] = [];
    const visit = (node: ReturnType<typeof parse>['ast']): void => {
        if (!node) {
            return;
        }
        if (node.type === 'and' || node.type === 'or') {
            node.children.forEach(visit);
        } else if (node.type === 'text' && node.value.kind !== 'regex' && node.value.text.replace(/\*/g, '').length >= 2) {
            words.push(...node.value.text.split('*').filter((part) => part.length >= 2));
        }
    };
    visit(parse(q).ast);

    return words;
}

export function renderText(text: string, options: { privacy: boolean; secrets?: readonly string[]; words?: readonly string[] }): ReactNode[] {
    const secrets = (options.secrets ?? []).filter((secret) => secret.length >= 6);
    const hidden = options.privacy ? new RegExp([PII.source, ...secrets.map(escapeRegex)].join('|'), 'gi') : null;
    const lit = options.words && options.words.length > 0 ? new RegExp(`(${options.words.map(escapeRegex).join('|')})`, 'gi') : null;
    const out: ReactNode[] = [];
    let key = 0;

    const light = (segment: string): void => {
        if (!lit) {
            out.push(segment);

            return;
        }
        segment.split(lit).forEach((part, index) => {
            out.push(index % 2 === 1 ? <mark key={key++}>{part}</mark> : part);
        });
    };

    if (!hidden) {
        light(text);

        return out;
    }
    let last = 0;
    for (const match of text.matchAll(hidden)) {
        light(text.slice(last, match.index));
        out.push(
            <span key={key++} className="redact">
                {'█'.repeat(match[0].length)}
            </span>,
        );
        last = (match.index ?? 0) + match[0].length;
    }
    light(text.slice(last));

    return out;
}

// The query bar's colours, from the parser's own tokens.
export function QueryHighlight({ q }: { q: string }): ReactNode {
    const { tokens } = parse(q);
    const out: ReactNode[] = [];
    let last = 0;
    tokens.forEach((token, index) => {
        out.push(q.slice(last, token.start));
        const kindClass = token.kind === 'phrase' ? 'hl-str' : token.kind === 'regex' ? 'hl-regex' : null;
        const className =
            token.type === 'field'
                ? 'hl-field'
                : token.type === 'operator'
                  ? 'hl-op'
                  : token.type === 'keyword'
                    ? 'hl-keyword'
                    : token.type === 'negation'
                      ? 'hl-neg'
                      : token.type === 'paren'
                        ? 'hl-paren'
                        : token.type === 'value'
                          ? (kindClass ?? 'hl-value')
                          : kindClass;
        out.push(
            <span key={index} className={className ?? undefined}>
                {q.slice(token.start, token.end)}
            </span>,
        );
        last = token.end;
    });
    out.push(q.slice(last));

    return <>{out}</>;
}
